import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';

// ── Helper : invalide tous les caches liés à un compte / tranche ──────────────

function invalidateAccountCaches(qc, accountId, trancheId) {
  qc.invalidateQueries({ queryKey: ['associate-accounts'] });
  if (accountId) {
    qc.invalidateQueries({ queryKey: ['associate-accounts', accountId] });
    qc.invalidateQueries({ queryKey: ['associate-accounts', accountId, 'tranches'] });
    if (trancheId) {
      qc.invalidateQueries({ queryKey: ['associate-accounts', accountId, 'tranches', trancheId] });
    }
  }
}

// ── Comptes ───────────────────────────────────────────────────────────────────

export function useAssociateAccounts() {
  return useQuery({
    queryKey: ['associate-accounts'],
    queryFn: () => api.get('/associate-accounts'),
    staleTime: 30_000,
  });
}

export function useAssociateAccount(id) {
  return useQuery({
    queryKey: ['associate-accounts', id],
    queryFn: () => api.get(`/associate-accounts/${id}`),
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/associate-accounts', body),
    onSuccess: () => {
      invalidateAccountCaches(qc);
      qc.invalidateQueries({ queryKey: ['associates'] });
    },
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/associate-accounts/${id}`, body),
    onSuccess: (_data, variables) => {
      invalidateAccountCaches(qc, variables.id);
      qc.invalidateQueries({ queryKey: ['associates'] });
    },
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/associate-accounts/${id}`),
    onSuccess: () => {
      invalidateAccountCaches(qc);
      qc.invalidateQueries({ queryKey: ['associates'] });
    },
  });
}

// ── Tranches ──────────────────────────────────────────────────────────────────

export function useTranches(accountId) {
  return useQuery({
    queryKey: ['associate-accounts', accountId, 'tranches'],
    queryFn: () => api.get(`/associate-accounts/${accountId}/tranches`),
    staleTime: 30_000,
    enabled: Boolean(accountId),
  });
}

export function useTranche(accountId, trancheId) {
  return useQuery({
    queryKey: ['associate-accounts', accountId, 'tranches', trancheId],
    queryFn: () => api.get(`/associate-accounts/${accountId}/tranches/${trancheId}`),
    staleTime: 30_000,
    enabled: Boolean(accountId) && Boolean(trancheId),
  });
}

export function useCreateTranche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, ...body }) =>
      api.post(`/associate-accounts/${accountId}/tranches`, body),
    onSuccess: (_data, variables) => {
      invalidateAccountCaches(qc, variables.accountId);
    },
  });
}

export function useUpdateTranche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, trancheId, ...body }) =>
      api.patch(`/associate-accounts/${accountId}/tranches/${trancheId}`, body),
    onSuccess: (_data, variables) => {
      invalidateAccountCaches(qc, variables.accountId, variables.trancheId);
    },
  });
}

export function useDeleteTranche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, trancheId }) =>
      api.delete(`/associate-accounts/${accountId}/tranches/${trancheId}`),
    onSuccess: (_data, variables) => {
      invalidateAccountCaches(qc, variables.accountId);
    },
  });
}

// ── Mouvements ────────────────────────────────────────────────────────────────

export function useCreateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, trancheId, ...body }) =>
      api.post(`/associate-accounts/${accountId}/tranches/${trancheId}/movements`, body),
    onSuccess: (_data, variables) => {
      invalidateAccountCaches(qc, variables.accountId, variables.trancheId);
    },
  });
}

export function useUpdateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, trancheId, movementId, ...body }) =>
      api.patch(
        `/associate-accounts/${accountId}/tranches/${trancheId}/movements/${movementId}`,
        body
      ),
    onSuccess: (_data, variables) => {
      invalidateAccountCaches(qc, variables.accountId, variables.trancheId);
    },
  });
}

export function useDeleteMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, trancheId, movementId }) =>
      api.delete(
        `/associate-accounts/${accountId}/tranches/${trancheId}/movements/${movementId}`
      ),
    onSuccess: (_data, variables) => {
      invalidateAccountCaches(qc, variables.accountId, variables.trancheId);
    },
  });
}

export function useLinkTransactionToMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, trancheId, movementId, transactionId }) =>
      api.post(
        `/associate-accounts/${accountId}/tranches/${trancheId}/movements/${movementId}/link-transaction`,
        { transaction_id: transactionId }
      ),
    onSuccess: (_data, variables) => {
      invalidateAccountCaches(qc, variables.accountId, variables.trancheId);
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
    },
  });
}

export function useUnlinkTransactionFromMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, trancheId, movementId }) =>
      api.delete(
        `/associate-accounts/${accountId}/tranches/${trancheId}/movements/${movementId}/link-transaction`
      ),
    onSuccess: (_data, variables) => {
      invalidateAccountCaches(qc, variables.accountId, variables.trancheId);
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
    },
  });
}
